const API_URL = import.meta.env.VITE_API_URL

function getToken() {
  try {
    const raw = localStorage.getItem('auth')
    const parsed = JSON.parse(raw)
    if (parsed?.token && parsed.expiresAt > Date.now()) {
      return parsed.token
    }
  } catch {
    return null
  }
  return null
}

function authHeaders(includeJson = true) {
  const token = getToken()
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (includeJson) headers['Content-Type'] = 'application/json'
  return headers
}

export async function getSubscription() {
  const res = await fetch(`${API_URL}/v2/subscription`, {headers: authHeaders()})
  if (!res.ok) throw new Error('Failed to fetch subscription')
  return await res.json()
}

export async function getSubscriptionBillingHistory() {
  const res = await fetch(`${API_URL}/v2/subscription/billing-history`, {headers: authHeaders()})
  if (!res.ok) throw new Error('Failed to fetch billing history')
  return await res.json()
}

export async function reconcileSubscription() {
  const res = await fetch(`${API_URL}/v2/subscription/reconcile`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }
  if (!res.ok) throw new Error(data?.error || 'Failed to reconcile subscription')
  return data
}

export async function getPlans(country) {
  const url = new URL(`${API_URL}/v2/subscription/plans`)
  if (country) url.searchParams.set('country', country)
  const res = await fetch(url, {headers: authHeaders()})
  if (!res.ok) throw new Error('Failed to fetch plans')
  return await res.json()
}

export async function createCheckoutSession(plan = 'full', promoCode) {
  const payload = {plan}
  if (promoCode) payload.promoCode = promoCode

  const res = await fetch(`${API_URL}/v2/subscription/checkout-session`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload)
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }
  if (!res.ok) throw new Error(data?.message || data?.error || 'Failed to create session')
  return data
}

export async function confirmSession(sessionId) {
  const res = await fetch(`${API_URL}/v2/subscription/confirm-session`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({sessionId})
  })

  let data
  try {
    data = await res.json()
  } catch {
    data = null
  }

  if (!res.ok) {
    throw new Error(data?.error || 'Failed to confirm session')
  }

  return data
}

export async function changePlan(plan) {
  const res = await fetch(`${API_URL}/v2/subscription/change-plan`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({plan})
  })
  if (!res.ok) throw new Error('Failed to change plan')
  return await res.json()
}

export async function cancelSubscription() {
  const res = await fetch(`${API_URL}/v2/subscription/cancel`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  })
  if (!res.ok) throw new Error('Failed to cancel subscription')
  return await res.json()
}

export async function cancelSubscriptionImmediate() {
  const res = await fetch(`${API_URL}/v2/subscription/cancel`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({immediate: true})
  })
  if (!res.ok) throw new Error('Failed to cancel subscription')
  return await res.json()
}

export async function resumeSubscription() {
  const res = await fetch(`${API_URL}/v2/subscription/resume`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  })
  if (!res.ok) throw new Error('Failed to resume subscription')
  return await res.json()
}

export async function createPortalSession() {
  const res = await fetch(`${API_URL}/v2/subscription/portal-session`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({})
  })
  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }
  if (!res.ok) throw new Error(data?.message || data?.error || 'Failed to create portal session')
  return data
}

export async function validatePromoCode(code, plan) {
  const res = await fetch(`${API_URL}/v2/promocodes/validate`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({code, plan})
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }

  if (!res.ok) {
    const error = new Error(data?.error || 'promo_invalid')
    error.code = data?.error
    error.promo = data?.promo || null
    throw error
  }

  return data
}

export async function applyPromoCode(promoCode) {
  const res = await fetch(`${API_URL}/v2/subscription/apply-promo`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({promoCode})
  })

  let data = null
  try {
    data = await res.json()
  } catch {
    data = null
  }

  if (!res.ok) {
    throw new Error(data?.message || data?.error || 'Failed to apply promo code')
  }

  return data
}
